import React, { useRef, useEffect, useCallback } from 'react';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  length?: number;
  disabled?: boolean;
}

export function LegacyOtpInput({
  value,
  onChange,
  onComplete,
  length = 6,
  disabled = false,
}: OtpInputProps) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hasCalledComplete = useRef(false);
  const onCompleteRef = useRef(onComplete);

  // Keep onComplete ref updated without causing re-renders
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Reset the hasCalledComplete flag when value changes to less than full length
  useEffect(() => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length < length) {
      hasCalledComplete.current = false;
    }
  }, [
    value,
    length,
  ]);

  // Auto-submit when all digits are filled (only once per complete code)
  useEffect(() => {
    const cleanValue = value.replace(/\D/g, '');
    if (
      cleanValue.length === length &&
      onCompleteRef.current &&
      !hasCalledComplete.current &&
      !disabled
    ) {
      hasCalledComplete.current = true;
      onCompleteRef.current(cleanValue);
    }
  }, [
    value,
    length,
    disabled,
  ]);

  const focusInput = (index: number) => {
    const ref = inputRefs.current[Math.max(0, Math.min(length - 1, index))];
    ref?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, length);
    if (digits) {
      onChange(digits);
      focusInput(Math.min(length - 1, digits.length - 1));
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) => {
    const newValue = e.target.value.replace(/\D/g, '');

    if (newValue.length > 1) {
      // Handle paste via input
      const pastedValue = newValue.slice(0, length);
      onChange(pastedValue);
      focusInput(Math.min(length - 1, pastedValue.length - 1));
      return;
    }

    // Handle single character
    const newOtp = value.split('');
    while (newOtp.length < length) newOtp.push('');

    newOtp[index] = newValue;
    const combinedValue = newOtp.join('').slice(0, length);

    onChange(combinedValue);

    if (newValue && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (!value[index] && index > 0) {
        focusInput(index - 1);
        const newOtp = value.split('');
        newOtp[index - 1] = '';
        onChange(newOtp.join('').slice(0, length));
      } else if (value[index]) {
        const newOtp = value.split('');
        newOtp[index] = '';
        onChange(newOtp.join('').slice(0, length));
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      focusInput(index - 1);
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      focusInput(index + 1);
    }
  };

  // Pad value to length for rendering
  const displayValue = value.padEnd(length, ' ').split('');

  return (
    <div
      className="flex gap-2 justify-center items-center"
      onPaste={handlePaste}
    >
      {displayValue.map((digit, index) => (
        <React.Fragment key={index}>
          <div className="relative">
            <input
              ref={(el) => (inputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              value={digit.trim()}
              onChange={(e) => handleChange(e, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onPaste={handlePaste}
              onFocus={(e) => e.target.select()}
              disabled={disabled}
              autoComplete="one-time-code"
              className="w-10 h-12 text-center text-xl font-semibold border border-gray-300 dark:border-gray-600 rounded-md shadow-sm dark:shadow-gray-900/50 focus-visible:border-primary-500 focus-visible:ring-1 focus-visible:ring-primary-500 outline-none transition-all bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-800 dark:bg-gray-800 dark:disabled:bg-gray-700 disabled:text-gray-400 dark:disabled:text-gray-500 dark:text-gray-400 placeholder-transparent dark:placeholder-gray-500"
              placeholder="○"
            />
            {!digit.trim() && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-gray-300 dark:text-gray-600 dark:text-gray-400 text-lg">
                ○
              </div>
            )}
          </div>
          {index === length / 2 - 1 && (
            <div className="w-2 h-1 bg-gray-300 dark:bg-gray-700 rounded-full mx-1" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}
